import { FunctionComponent, useContext, useEffect, useState } from 'react'
import { Stack, TextField, Typography } from '@mui/material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import moment from 'moment'
import { DialogWrapper } from '../display-elements-components/dialog-wrapper'
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications'
import ReadinessService from '../../../shared/services/readiness/readiness.service'
import { IReadinessFinding } from '../../../shared/services/readiness/readiness.model'

// Acknowledging is the deliberate alternative to fixing: it records a reason (and an optional
// review date) that is disclosed in the assumptions register and the audit pack. A finding can
// never be ticked off; this is the only way to quieten one without changing the data.
export const AcknowledgeFindingDialog: FunctionComponent<{
  open: boolean,
  finding: IReadinessFinding | null,
  reportPeriodId: string,
  closeHandler: () => void,
  onAcknowledged: () => void,
}> = ({
  open,
  finding,
  reportPeriodId,
  closeHandler,
  onAcknowledged,
}) => {

    const userNotificationsContext = useContext(UserNotificationsContext)
    const readinessService = new ReadinessService()
    const [reason, setReason] = useState('')
    const [expiresAt, setExpiresAt] = useState('')
    const [isSubmitting, setIsSubmitting] = useState(false)

    useEffect(() => {
      if (open) {
        setReason('')
        setExpiresAt('')
        setIsSubmitting(false)
      }
    }, [open, finding?.checkId, finding?.entityKey])

    const submitHandler = () => {
      if (!finding || !reason.trim()) return
      setIsSubmitting(true)
      readinessService.acknowledgeFinding(reportPeriodId, {
        checkId: finding.checkId,
        entityKey: finding.entityKey,
        reason: reason.trim(),
        expiresAt: expiresAt || null,
      })
        .then(() => {
          userNotificationsContext.openSnackbar({ open: true, message: 'Finding acknowledged' })
          closeHandler()
          onAcknowledged()
        })
        .catch(err => {
          const message = err?.response?.data?.message || 'There was an error, please try again'
          userNotificationsContext.openSnackbar({ open: true, message, backgroundColor: 'text.primary' })
        })
        .finally(() => setIsSubmitting(false))
    }

    return (
      <DialogWrapper
        open={open}
        onClose={closeHandler}
        title='Acknowledge finding'
        cancelHandler={closeHandler}
        submitHandler={submitHandler}
        isSubmitting={isSubmitting}
        disableSaveButton={!reason.trim()}
        submitButtonText='Acknowledge'
        maxWidth='sm'
      >
        <Stack spacing={2}>
          {finding && <Typography variant='subtitle1' sx={{ fontWeight: 600 }}>{finding.title}</Typography>}
          <Typography variant='body2' sx={{ color: 'text.secondary' }}>
            Acknowledging records this as a known, explained item. It is listed under Acknowledged on this page, disclosed in your generated reports and audit pack, and it will reopen if the data changes or the review date passes.
          </Typography>
          <TextField
            label='Reason'
            required
            multiline
            minRows={3}
            fullWidth
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder='e.g. The site closed for refurbishment in June and July, so no energy was used.'
            helperText='Disclosed in your generated reports and audit pack.'
          />
          {/* The app's date picker (as in esos-action-dialog.tsx), not the browser's native one, with
              the app's clear icon for an optional date (edit-upload-line-spend-based-dialog.tsx). A
              review date in the past would reopen the finding at once, so past days are disabled. The
              server still receives YYYY-MM-DD. */}
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <DatePicker
              label='Review by (optional)'
              value={expiresAt ? moment(expiresAt) : null}
              onChange={(value: any) => setExpiresAt(value && moment(value).isValid() ? moment(value).format('YYYY-MM-DD') : '')}
              format='DD/MM/YYYY'
              disablePast
              slotProps={{ textField: { fullWidth: true }, field: { clearable: true } }}
            />
          </LocalizationProvider>
        </Stack>
      </DialogWrapper>
    )
  }
